import { skipToken } from "@reduxjs/toolkit/query";
import type {
  ProjectSettings,
  ProjectWithInterviewCount,
} from "@repo/app-types/entities/project";
import { ProjectSchedulingMode } from "@repo/app-types/entities/project";
import { getAvailabilitySummary } from "@repo/app-types/entities/schedulingConfiguration";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Label } from "@repo/ui/Label/label";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { Size } from "@repo/ui/helpers/helpers";
import { IconCalendar } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { useSchedulingConfigurationByIdQuery } from "../../../../api/entities/schedulingConfiguration.js";
import { Can } from "../../../../helpers/teammateAuthorizationContext.js";
import { SchedulingAvailabilitySlideover } from "../scheduling/schedulingAvailabilitySlideover.js";

interface JobOpeningSchedulingSettingProps {
  project: ProjectWithInterviewCount;
  onSaveProject: (project: {
    id: string;
    settings?: ProjectSettings;
  }) => Promise<void>;
}

export const JobOpeningSchedulingSetting: FC<
  JobOpeningSchedulingSettingProps
> = ({ project, onSaveProject }) => {
  const { scheduling_mode, scheduling_configuration_id } = project.settings;

  const { data: existingConfig, isLoading } =
    useSchedulingConfigurationByIdQuery(
      scheduling_configuration_id ?? skipToken,
    );

  const [isOpen, setIsOpen] = useState(false);

  const hasNoAvailability =
    !existingConfig?.settings.weekly_hours ||
    Object.values(existingConfig.settings.weekly_hours).every(
      (slots) => slots.length === 0,
    );

  return (
    <div className="mt-6">
      <Can
        I={PermissionActions.UPDATE}
        a={PermissionSubjects.JOB_OPENINGS}
        passThrough={true}
      >
        {(allowed) => (
          <div className="flex flex-col gap-2">
            <Label size={Size.SMALL}>Scheduling</Label>
            {isLoading ? (
              <div className="mb-2 flex items-center space-x-2">
                <Loader style={LoaderStyle.ORBIT} />
              </div>
            ) : (
              <div>
                {project.settings.scheduling_mode ? (
                  <div className="mb-2 flex flex-col gap-1 text-sm text-muted-foreground">
                    <div>
                      <span>
                        {project.settings.scheduling_mode ===
                        ProjectSchedulingMode.CollectAvailability
                          ? "The AI interviewer will find the candidate's availability for a next meeting, making it easy to schedule the next interview if they are advanced."
                          : "The AI interviewer will find the candidate's availability and send a calendar invite if they are advanced."}
                      </span>
                    </div>
                    {hasNoAvailability ? (
                      <div className="text-destructive">
                        Add availability below to enable scheduling.
                      </div>
                    ) : (
                      <div>
                        Availability:{" "}
                        {getAvailabilitySummary(
                          existingConfig?.settings.weekly_hours,
                        )}
                      </div>
                    )}
                  </div>
                ) : null}
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => setIsOpen(true)}
                  isDisabled={!allowed}
                  icon={<IconCalendar className="h-4 w-4" />}
                >
                  Edit scheduling settings
                </Button>
                {/* TODO: This is a hack to ensure the slideover state fully resets on open/close */}
                {isOpen && (
                  <SchedulingAvailabilitySlideover
                    project={project}
                    existingSchedulingConfig={existingConfig ?? null}
                    companyId={project.company_id}
                    onSaveProject={onSaveProject}
                    onClose={() => setIsOpen(false)}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </Can>
      {!scheduling_mode ? (
        <div className="mt-2 text-sm text-muted-foreground">
          Collect the candidate's availability during the interview to speed up
          scheduling of the next interview.
        </div>
      ) : null}
    </div>
  );
};
